import {
  HTTP_INTERCEPTORS,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { EnvironmentService } from '../environment.service';

export const SKIP_CACHE_HEADER = 'X-Skip-Cache';

@Injectable()
class CachingInterceptor implements HttpInterceptor {
  private cache = new Map<string, any>();

  private get maxAgeOfCache(): number {
    //Cache duration in milliseconds,
    return this.envService.environments.maxAgeOfCache ?? 300000; // 5 minutes
  }

  private envService: EnvironmentService = inject(EnvironmentService);

  private getCache(key: string): HttpResponse<any> | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.expiry > this.maxAgeOfCache) {
      this.cache.delete(key); // Remove expired cache entry
      return null;
    }

    return entry.response;
  }

  private setCache(key: string, value: HttpResponse<any>): void {
    const expiry = Date.now();
    this.cache.set(key, { response: value, expiry });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.method !== 'GET' || request.headers.get(SKIP_CACHE_HEADER)) {
      const newReq = request.clone({
        headers: request.headers.delete(SKIP_CACHE_HEADER),
      });
      return next.handle(newReq);
    }
    const cachedResponse = this.getCache(request.urlWithParams);
    if (cachedResponse) {
      return of(cachedResponse);
    }

    return next.handle(request).pipe(
      tap(response => {
        if (response instanceof HttpResponse) {
          this.setCache(request.urlWithParams, response);
        }
      }),
    );
  }
}

export const cachingInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
];
